import React, {useCallback, useEffect} from "react";
import TreeView from '@material-ui/lab/TreeView';
import {useFormik} from "formik";
import TextField from "@material-ui/core/TextField";
import {Grid} from "@material-ui/core";
import Chip from '@material-ui/core/Chip';
import Autocomplete from '@material-ui/lab/Autocomplete';
import styled from "styled-components";
import {makeStyles} from '@material-ui/core/styles';
import {RootState} from "../store/root-reducer";
import {useSelector} from "react-redux";
import {CategoryCsvModel} from "../models/category-csv.model";
import {CategoryModel} from "../models/category.model";
import {StyledButton} from "./ui-base/button";
import {StyledCheckbox} from "./form/checkbox";
import {StyledTreeItem, StyledTreeMinusSquare, StyledTreePlusSquare} from "./shared/styled-tree";
import {useTranslation} from "react-i18next";

const useStyles = makeStyles({
    root: {},
    checkbox: {
        paddingTop: 9,
        paddingBottom: 9,
        paddingRight: 5,
        paddingLeft: 5,
        marginLeft: -3
    }
});

export const Categories = ({mainCategory, handleSelectedCategory, selectedCategory}) => {
    const [selected, setSelected] = React.useState<CategoryModel[]>([]);
    const [expanded, setExpanded] = React.useState<string[]>([]);
    const [tree, setTree] = React.useState<CategoryModel[]>([]);
    const [origTree, setOrigTree] = React.useState<CategoryModel[]>([]);
    const [nodeSelected, setNodeSelected] = React.useState<CategoryModel>(null);
    const [filteredCategories, setFilteredCategories] = React.useState<CategoryModel[]>([]);
    const { t } = useTranslation(['author', 'common']);

    const themaCategories: CategoryModel[] = useSelector(
        (state: RootState) => state.categories.categories
    )

    const categoriesCsv: CategoryCsvModel[] = useSelector(
        (state: RootState) => state.categories.categoriesCsv
    );

    const classes = useStyles();

    const getCategoriesCodeByMainCategory = useCallback(() => {
        return categoriesCsv.filter((category) => category[mainCategory] === 'TRUE' && category['Do wyrzucenia'] === 'FALSE').map((category) => category.Code);
    }, [categoriesCsv, mainCategory]);

    useEffect(() => {
        if (selectedCategory && (!selected || selected.length === 0)) {
            setSelected(selectedCategory);
        }
    }, [selectedCategory, selected])

    useEffect(() => {
        const setChildren = (parent: CategoryModel) => {
            if (parent && !parent.children) {
                parent.children = [{
                    ...parent,
                    id: parent.id,
                    name: t('categoriesComponent.general'),
                    description: t('categoriesComponent.general'),
                    children: [],
                    code: parent.code + '_GENERALCATEGORY',
                    parent_code: parent.code
                }];
            }
        }

        if (mainCategory && themaCategories && themaCategories.length > 0) {
            const tmpThemaCategories = JSON.parse(JSON.stringify(themaCategories));
            const codes: string[] = getCategoriesCodeByMainCategory();
            const filteredCategories = tmpThemaCategories.filter((category) => codes.includes(category.code));
            setFilteredCategories(filteredCategories);
            setOrigTree([]);
            setTree([]);
            setNodeSelected(null);
            setExpanded([]);

            const categoriesReady = filteredCategories.filter((category) => {
                return !category.parent_code;
            }).map(category => {
                setChildren(category);
                return category;
            });

            const categoryWithParent = filteredCategories.filter((category) => category.parent_code);

            categoryWithParent.forEach((category: CategoryModel) => {
                const codeParentArr: string[] = category.parent_code.split('');
                let codeParent = '';
                let parent: CategoryModel;

                codeParentArr.forEach((value, i) => {
                    codeParent += value;

                    if (i === 0) {
                        parent = categoriesReady.find((res) => res.code === codeParent);
                        if (!parent) {
                            const newCategory = tmpThemaCategories.find((themaCat) => themaCat.code === codeParent);
                            setChildren(newCategory);
                            categoriesReady.push(newCategory);
                            parent = newCategory;
                        }
                    } else {
                        setChildren(parent);
                        const tmpParent = parent.children.find((res) => res.code === codeParent);

                        if (!tmpParent) {
                            parent.children.push(tmpThemaCategories.find((themaCat) => themaCat.code === codeParent));
                            const tmpParent = parent.children.find((res) => res.code === codeParent);
                            setChildren(tmpParent);

                            parent = tmpParent;
                        } else {
                            parent = tmpParent;
                        }
                    }
                })

                setChildren(parent);

                if (parent) {
                    if (parent.children.indexOf(category) === -1) {
                        parent.children.push(category);
                    }
                }
            });

            setOrigTree(categoriesReady);
            setTree(categoriesReady);
        }
    }, [mainCategory, themaCategories, getCategoriesCodeByMainCategory, t]);

    const getChildById = (node: CategoryModel, id: string) => {
        let array: string[] = [];

        const getAllChild = (nodes: CategoryModel | null) => {
            if (nodes === null) return [];
            array.push(nodes.code);
            if (Array.isArray(nodes.children)) {
                nodes.children.forEach(node => {
                    array = [...array, ...getAllChild(node)];
                    array = array.filter((v, i) => array.indexOf(v) === i);
                });
            }
            return array;
        }

        const getNodeById = (nodes: CategoryModel, id: string) => {
            if (nodes.code === id) {
                return nodes;
            } else if (Array.isArray(nodes.children)) {
                let result = null;
                nodes.children.forEach(node => {
                    if (!!getNodeById(node, id)) {
                        result = getNodeById(node, id);
                    }
                });
                return result;
            }

            return null;
        }

        return getAllChild(getNodeById(node, id));
    }

    const getOnChange = (checked: boolean, nodes: CategoryModel) => {
        const array = checked ? [...selected, nodes] : selected.filter(value => value.code !== nodes.code);

        setSelected(array);
        handleSelectedCategory(array);
    }

    const getTreeItemsFromData = (treeItems: CategoryModel[]) => {
        return treeItems.map(treeItemData => {
            let children = undefined;
            if (treeItemData.children && treeItemData.children.length > 0) {
                children = getTreeItemsFromData(treeItemData.children);
            }
            return (
                <StyledTreeItem
                    key={treeItemData.code}
                    // @ts-ignore
                    nodeId={treeItemData.code}
                    label={
                        !treeItemData.children || treeItemData.children.length === 0 ?
                            <StyledCheckbox
                                id={''}
                                name={''}
                                onChange={event =>
                                    getOnChange(event.currentTarget.checked, treeItemData)
                                }
                                value={selected.some(item => item.code === treeItemData.code)}
                                label={treeItemData.name}
                                onClick={e => e.stopPropagation()}
                                className={classes.checkbox}
                                key={treeItemData.code}
                            />
                            :
                            <StyledTreeLabelWithoutCheckbox>
                                {treeItemData.name}
                            </StyledTreeLabelWithoutCheckbox>

                    }
                    children={children}/>
            );
        });
    };

    const DataTreeView = () => {
        return (
            <TreeView
                defaultCollapseIcon={<StyledTreeMinusSquare/>}
                defaultExpandIcon={<StyledTreePlusSquare/>}
                expanded={expanded}
                onNodeToggle={handleToggle}
            >
                {getTreeItemsFromData(tree)}
            </TreeView>
        );
    };

    const dfs = (node: CategoryModel, term, foundIDS) => {
        // Implement your search functionality
        let isMatching = node.name && node.name.toLowerCase().indexOf(term.toLowerCase()) > -1;

        if (Array.isArray(node.children)) {
            node.children.forEach((child) => {
                const hasMatchingChild = dfs(child, term, foundIDS);
                isMatching = isMatching || hasMatchingChild;
            });
        }

        // We will add any item if it matches our search term or if it has a children that matches our term
        if (isMatching && node.code) {
            foundIDS.push(node.code);
            foundIDS.push(node.code + '_GENERALCATEGORY');
        }

        return isMatching;
    }

    const filter = (data, matchedIDS) => {
        return data
            .filter((item) => matchedIDS.indexOf(item.code) > -1)
            .map((item) => ({
                ...item,
                children: item.children ? filter(item.children, matchedIDS) : [],
            }));
    }

    const search = (term, returnIds = false) => {
        if (term) {
            const dataNode = {
                children: origTree,
            };

            const matchedIDS = [];
            dfs(dataNode as CategoryModel, term, matchedIDS);

            setExpanded(matchedIDS);
            searchForm.setFieldValue('search', '');

            if (!returnIds) {
                return filter(origTree, matchedIDS);
            }

            return matchedIDS;
        } else {
            handleSelectedCategory([]);
            setExpanded([]);
            return [...origTree];
        }

    }

    const searchForm = useFormik({
        initialValues: {
            search: ''
        },
        onSubmit: async values => {
            setTree(search(values.search.toLowerCase()));
        }
    })

    const handleToggle = (event: React.ChangeEvent<{}>, nodeIds: string[]) => {
        setExpanded(nodeIds);
    };

    const getChipsLabelByCode = (codeModel: CategoryModel) => {
        let ids = codeModel.code.replace('_GENERALCATEGORY', '').split('');
        if (codeModel.code.indexOf('_GENERALCATEGORY') === -1) {
            ids.pop();
        }
        let codeToSearch = '';
        let label = '';

        ids.forEach(id => {
            codeToSearch += id;

            label += themaCategories.find((category) => category.code === codeToSearch).name + ' -> ';
        });

        label += codeModel.name;

        return label;
    }

    const searchOnChange = (event, value: CategoryModel | string) => {
        if (typeof value === 'string') {
            setTree(search(value.toLowerCase()));
            setNodeSelected(null);
        } else if ((typeof value === 'object' && !value.children) || value.children.length === 0) {
            handleSelectedCategory([...selected, value]);
            setSelected([...selected, value]);
            setTree([...origTree]);
            searchForm.setFieldValue('search', '');
            setNodeSelected(null);
        } else {
            setNodeSelected(value);
            searchForm.setFieldValue('search', value.name);

            let childrenCodes = [];

            origTree.forEach((val) => {
                childrenCodes = [...childrenCodes, ...getChildById(val, value.code)];
            })

            const searchedCodes = search(value.name, true);

            let matchedIDS = childrenCodes.concat(searchedCodes);
            matchedIDS = matchedIDS.filter((item, index) => {
                return (matchedIDS.indexOf(item) === index)
            });

            setTree(filter(origTree, matchedIDS));
        }
    }

    const getFormValue = () => {
        return searchForm.values.search;
    }

    return (
        <>
            <Grid container direction={'row'} alignItems={"center"} style={{marginTop: '-20px'}}>
                <Grid item xs={true}>
                    <Autocomplete
                        freeSolo
                        id="free-solo-2-demo"
                        disableClearable
                        options={filteredCategories}
                        getOptionLabel={option => (typeof option === 'object') ? option.name : option}
                        value={nodeSelected}
                        onChange={searchOnChange}
                        inputValue={getFormValue()}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                id={'search'}
                                name={'search'}
                                label={t('categoriesComponent.search')}
                                margin="normal"
                                onChange={(event) => {
                                    setNodeSelected(null);
                                    searchForm.handleChange(event);
                                }}
                            />
                        )}
                    />
                </Grid>

                <Grid item xs={false}>
                    <StyledButton type="button" color={"violet"} onClick={() => searchForm.submitForm()}>
                        {t('common:search')}
                    </StyledButton>
                </Grid>
            </Grid>

            <Grid container direction={"row"} spacing={1}>
                {selected.map((code) => {
                    return (
                        <Grid item key={code.code} style={{maxWidth: '100%'}}>
                            <Chip
                                style={{marginBottom: 10, maxWidth: '100%'}}
                                label={getChipsLabelByCode(code)}
                                onDelete={() => {
                                    handleSelectedCategory(selected.filter(sel => sel !== code));
                                    setSelected(selected.filter(sel => sel !== code));
                                }}
                            />
                        </Grid>
                    );
                })}
            </Grid>

            <StyledTreeWrapper style={{marginTop: '10px'}}>{DataTreeView()}</StyledTreeWrapper>
        </>
    )
}

const StyledTreeLabelWithoutCheckbox = styled('div')`
  padding: 9px 0;
`;

const StyledTreeWrapper = styled('div')`
  margin-top: 50px;
  max-height: 500px;
  overflow-y: auto;
`
